'use client';

import { forwardRef, useEffect, useRef, useId, useCallback } from 'react';
import styles from './Textarea.module.css';
import { getClasses } from '@/_utils/classnames';
import { TextareaProps } from './Textarea.types';
import { Flex, Text } from '@/_components';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      helperText,
      onKeyPress,
      onKeyDown,
      isDisabled,
      maxLength,
      minLength,
      isLabelHidden = false,
      isRequired,
      rows = 4,
      label,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      value,
      'data-testid': dataTestId,
    },
    forwardedRef,
  ) => {
    const internalRef = useRef<HTMLTextAreaElement | null>(null);
    const id: string = useId();
    const inputClasses: string = getClasses(styles.textarea, {
      [styles.textareaDisabled]: isDisabled,
    });

    // Combine internal ref with forwarded ref
    const combinedRef = useCallback(
      (node: HTMLTextAreaElement | null) => {
        internalRef.current = node;
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef],
    );

    useEffect(() => {
      if (internalRef?.current) {
        internalRef.current.style.height = 'inherit';
        internalRef.current.style.height = `${internalRef.current.scrollHeight}px`;
      }
    }, [value]);

    const handleInputChange = (
      event: React.ChangeEvent<HTMLTextAreaElement>,
    ) => {
      onChange?.(event);

      if (internalRef?.current) {
        internalRef.current.style.height = 'inherit';
        internalRef.current.style.height = `${internalRef.current.scrollHeight}px`;
      }
    };

    return (
      <Flex isColumn={true} gap={2} className={styles.textareaContainer}>
        {!isLabelHidden && (
          <label htmlFor={id} className="flex gap-2 flex-y-center">
            <Text
              type="small"
              fontWeight="medium"
              color={isDisabled ? 'secondary' : 'primary'}
            >
              {label}
            </Text>
            {isRequired && (
              <Text type="small" color="critical">
                *
              </Text>
            )}
          </label>
        )}
        <Flex className={styles.textareaContainer}>
          <textarea
            value={value}
            className={inputClasses}
            placeholder={placeholder}
            maxLength={maxLength}
            ref={combinedRef}
            minLength={minLength}
            onBlur={onBlur}
            onChange={handleInputChange}
            onKeyPress={onKeyPress}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            rows={rows}
            id={id}
            disabled={!!isDisabled}
            data-testid={dataTestId}
          />
        </Flex>
        {!!helperText && (
          <Flex isYCentered={true} gap={2}>
            <Text type="tiny" color={isDisabled ? 'disabled' : 'secondary'}>
              {helperText}
            </Text>
          </Flex>
        )}
      </Flex>
    );
  },
);

Textarea.displayName = 'Textarea';
